import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SOURCE_NAME = 'anticatrade-extension';
const BRIDGE_SOURCE_NAME = 'anticatrade-extension-bridge';

interface PublishJob {
	productID: number;
	merchantIntegrationID: number;
	integrationKey: string;
}

interface ExtensionPublishResult {
	job: PublishJob;
	ok: boolean;
	externalID?: string;
	error?: string;
	errorCode?: string;
}

const useExtension = (onResult: (result: ExtensionPublishResult) => void) => {
	const [isExtensionAvailable, setIsExtensionAvailable] = useState(false);

	const { i18n } = useTranslation();

	// Keep the latest callback without re-subscribing the listener.
	const onResultRef = useRef(onResult);

	onResultRef.current = onResult;

	useEffect(() => {
		// Listen for messages from the Anticatrade browser extension
		// bridge to detect whether the extension is installed, then
		// keep listening for any new publications.
		const onMessage = (event: MessageEvent) => {
			if (event.source !== window) {
				return;
			}

			const data = event.data;

			if (data == null || data.source !== BRIDGE_SOURCE_NAME) {
				return;
			}

			if (data.type === 'READY' || data.type === 'PONG') {
				setIsExtensionAvailable(true);
			} else if (data.type === 'PUBLISH_RESULT') {
				onResultRef.current({
					job: data.job,
					ok: data.ok,
					externalID: data.externalID,
					error: data.error,
					errorCode: data.errorCode,
				});
			}
		};
		window.addEventListener('message', onMessage);
		window.postMessage({ source: PAGE_SOURCE_NAME, type: 'PING' }, window.origin);
		return () => window.removeEventListener('message', onMessage);
	}, []);

	const requestPublish = (job: PublishJob) => {
		window.postMessage(
			{
				source: PAGE_SOURCE_NAME,
				type: 'PUBLISH_REQUEST',
				job: { ...job, language: i18n.language },
			},
			window.origin,
		);
	};

	return { isExtensionAvailable, requestPublish };
};

export { useExtension };
export type { PublishJob, ExtensionPublishResult };
